import React from "react";
import { Edit, Trash2, CheckCircle, XCircle } from "lucide-react";

interface MobileMenuManagementProps {
    filteredMenu: any[];
    loading: boolean;
    openEditModal: (item: any) => void;
    handleDelete: (id: string) => void;
    handleOutOfStock: (item: any) => void;
    loadingIds: Set<string>;
    outOfStockIcon?: string;
}

const MobileMenuManagement: React.FC<MobileMenuManagementProps> = ({
    filteredMenu,
    loading,
    openEditModal,
    handleDelete,
    handleOutOfStock,
    loadingIds,
    outOfStockIcon,
}) => {
    if (loading)
        return <p className="text-center py-4 text-gray-500">Loading menu...</p>;

    if (filteredMenu.length === 0)
        return <p className="text-center py-4 text-gray-500">No menu items found.</p>;

    return (
        <div className="grid grid-cols-1 gap-3 md:hidden">
            {filteredMenu.map((item) => {
                const isAvailable = item.isAvailable ?? true;

                return (
                    <div
                        key={item.id}
                        className="flex items-center bg-white dark:bg-gray-800 rounded-xl shadow p-3 gap-3 relative"
                    >
                        {/* Thumbnail */}
                        <div className="relative flex-shrink-0">
                            <img
                                src={item.imageUrl || "https://placehold.co/80x80"}
                                alt={item.name}
                                className={`w-16 h-16 rounded-lg object-cover ${!isAvailable ? "opacity-60" : ""
                                    }`}
                            />
                            {!isAvailable && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
                                    <XCircle className="text-red-400" size={20} />
                                </div>
                            )}
                        </div>

                        {/* Details */}
                        <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                                {item.name}
                            </h4>

                            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                                {item.shortDescription ?? item.description ?? ""}
                            </p>

                            {/* Price Row */}
                            <div className="flex items-center justify-between mt-1">
                                <span className="text-orange-600 font-semibold text-sm">
                                    ₹{item.discountPrice ?? item.price}
                                </span>
                                {item.discountPrice && (
                                    <span className="text-gray-400 line-through text-xs ml-1">
                                        ₹{item.price}
                                    </span>
                                )}
                            </div>

                            {/* Availability Flag */}
                            <div className="flex items-center gap-1 mt-1 text-xs">
                                {isAvailable ? (
                                    <>
                                        <CheckCircle size={14} className="text-green-500" />
                                        <span className="text-green-600 font-medium">Available</span>
                                    </>
                                ) : (
                                    <>
                                        <XCircle size={14} className="text-red-500" />
                                        <span className="text-red-600 font-medium">Out of Stock</span>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2 ml-2 items-center">
                            {/* Edit */}
                            <button
                                onClick={() => openEditModal(item)}
                                className="text-blue-500 hover:text-blue-700"
                            >
                                <Edit size={18} />
                            </button>

                            {/* Delete */}
                            <button
                                onClick={() => handleDelete(item.id)}
                                className="text-red-500 hover:text-red-700"
                            >
                                <Trash2 size={18} />
                            </button>

                            {/* Out-of-stock toggle */}
                            <button
                                onClick={() => handleOutOfStock(item)}
                                disabled={loadingIds.has(item.id)}
                                title={
                                    item.isAvailable
                                        ? "Mark as Out of Stock"
                                        : "Mark as Available"
                                }
                                className="relative p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            >
                                {loadingIds.has(item.id) ? (
                                    <span className="block w-5 h-5 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></span>
                                ) : (
                                    <img
                                        src={
                                            outOfStockIcon ||
                                            "https://cdn-icons-png.flaticon.com/512/1828/1828843.png"
                                        }
                                        alt="Out of Stock"
                                        className={`w-5 h-5 ${item.isAvailable ? "" : "opacity-50"
                                            }`}
                                    />
                                )}
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default MobileMenuManagement;
